import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  MenuItem,
  Chip,
  useTheme,
  TextField,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Skeleton,
  Pagination,
  Divider,
  Tooltip,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Delete,
  Add,
  CalendarToday,
  LocationOn,
  Link as LinkIcon,
  EventSeat,
  Lock,
  Public,
  WorkspacePremium,
  ToggleOn,
  ToggleOff,
  Visibility,
  Search,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { eventService } from '@/services/events/eventService';
import {
  Business,
  ConferenceEvent,
  EVENT_CATEGORIES,
  CreateEventRequest,
  UpdateEventRequest,
} from '@/constants/types';
import toast from 'react-hot-toast';
import { ConfirmDialog } from '@/components/common';
import { EventDialog } from '@/components/events/EventDialog';
import { useAppDispatch, useAppSelector } from '@/store';
import { createEvent, updateEvent } from '@/store';

const formatDate = (iso?: string) => {
  if (!iso) return '—';
  const date = new Date(iso);
  const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
  const day = date.toLocaleDateString('en-US', { day: '2-digit' });
  const month = date.toLocaleDateString('en-US', { month: 'long' });
  const time = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  return `${weekday} – ${day} ${month} at ${time}`;
};

interface EventCardProps {
  event: ConferenceEvent;
  onView: (event: ConferenceEvent) => void;
  onEdit: (event: ConferenceEvent) => void;
  onDelete: (event: ConferenceEvent) => void;
  onToggleActive: (event: ConferenceEvent) => void;
}

const EventCard: React.FC<EventCardProps> = ({ event, onView, onEdit, onDelete, onToggleActive }) => {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      onClick={() => onView(event)}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s',
        cursor: 'pointer',
        '&:hover': { boxShadow: theme.shadows[4] },
      }}
    >
      {event.eventBanner?.url ? (
        <CardMedia
          component="img"
          height={160}
          image={event.eventBanner.url}
          alt={event.eventName}
          sx={{ objectFit: 'cover' }}
        />
      ) : (
        <Box
          sx={{
            height: 160,
            bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CalendarToday sx={{ fontSize: 48, color: 'text.disabled' }} />
        </Box>
      )}

      <CardContent sx={{ flex: 1, pb: 1 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          <Chip
            label={event.category}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
          <Chip
            label={event.eventMode === 'online' ? 'Online' : 'In Person'}
            color={event.eventMode === 'online' ? 'info' : 'default'}
            size="small"
            sx={{ fontSize: '0.7rem' }}
          />
          <Chip
            label={event.ticketType === 'free' ? 'Free' : `₹${event.price}`}
            color={event.ticketType === 'free' ? 'success' : 'warning'}
            size="small"
            sx={{ fontSize: '0.7rem' }}
          />
          {!event.isActive && (
            <Chip label="Inactive" size="small" color="default" sx={{ fontSize: '0.7rem' }} />
          )}
        </Box>

        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{
            mb: 1,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {event.eventName}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
          <CalendarToday sx={{ fontSize: 14, color: 'text.secondary', mt: '2px' }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, minWidth: 32 }}>Start</Typography>
              <Typography variant="caption" color="text.secondary">{formatDate(event.startDateTime)}</Typography>
            </Box>
          </Box>
        </Box>

        {event.eventMode === 'in_person' && event.venueName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {event.venueName}
            </Typography>
          </Box>
        )}
        {event.eventMode === 'online' && event.eventLink && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <LinkIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="primary" noWrap>
              {event.eventLink}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
          {event.totalSeats && (
            <Tooltip title="Total Seats">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <EventSeat sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">{event.totalSeats}</Typography>
              </Box>
            </Tooltip>
          )}
          {event.enrollmentCount !== undefined && (
            <Tooltip title="Enrolled Users">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <Typography variant="caption" color="success.main" fontWeight={600}>
                  {event.enrollmentCount} enrolled
                </Typography>
              </Box>
            </Tooltip>
          )}
          <Tooltip title={`Visibility: ${event.eventVisibility}`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              {event.eventVisibility === 'public'
                ? <Public sx={{ fontSize: 14, color: 'text.secondary' }} />
                : <Lock sx={{ fontSize: 14, color: 'text.secondary' }} />}
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                {event.eventVisibility}
              </Typography>
            </Box>
          </Tooltip>
          {event.views !== undefined && (
            <Tooltip title="View Count">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <Visibility sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {event.views}
                </Typography>
              </Box>
            </Tooltip>
          )}
          {event.certificateProvided && (
            <Tooltip title="Certificate Provided">
              <WorkspacePremium sx={{ fontSize: 14, color: 'success.main' }} />
            </Tooltip>
          )}
        </Box>
      </CardContent>

      <Divider />

      <CardActions sx={{ px: 2, py: 1.5, justifyContent: 'space-between' }} onClick={(e) => e.stopPropagation()}>
        <Tooltip title={event.isActive ? 'Mark Inactive' : 'Activate Event'}>
          <IconButton
            size="small"
            onClick={() => onToggleActive(event)}
            sx={{ color: event.isActive ? 'success.main' : 'text.disabled' }}
          >
            {event.isActive ? <ToggleOn fontSize="large" /> : <ToggleOff fontSize="large" />}
          </IconButton>
        </Tooltip>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(event)} sx={{ color: 'primary.main' }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => onDelete(event)} sx={{ color: 'error.main' }}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardActions>
    </Card>
  );
};

const CardSkeleton: React.FC = () => (
  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
    <Skeleton variant="rectangular" height={160} />
    <CardContent>
      <Skeleton width="60%" height={24} sx={{ mb: 1 }} />
      <Skeleton width="40%" height={20} sx={{ mb: 0.5 }} />
      <Skeleton width="80%" height={20} sx={{ mb: 0.5 }} />
      <Skeleton width="50%" height={20} />
    </CardContent>
  </Card>
);

interface BusinessEventsTabProps {
  business: Business;
}

export const BusinessEventsTab: React.FC<BusinessEventsTabProps> = ({ business }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isCreating, isUpdating } = useAppSelector((state) => state.events);

  const [events, setEvents] = useState<ConferenceEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 9;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ConferenceEvent | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<ConferenceEvent | null>(null);

  useEffect(() => {
    loadEvents();
  }, [categoryFilter, statusFilter, page, searchQuery, business._id]);

  useEffect(() => {
    setPage(1);
  }, [categoryFilter, statusFilter, searchQuery, business._id]);

  const loadEvents = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const params: any = { page, limit: pageSize, businessId: business._id };
      if (searchQuery) params.search = searchQuery;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (statusFilter === 'active') params.isActive = true;
      if (statusFilter === 'inactive') params.isActive = false;

      const response = await eventService.getMyEvents(params);
      const filtered = (response.data.events || []).filter((e) => {
        const id = typeof e.businessId === 'object' ? e.businessId?._id : e.businessId;
        return id === business._id;
      });
      setEvents(filtered);
      setTotalPages(Math.ceil(response.data.pagination.totalItems / pageSize));
    } catch (err: any) {
      setError(err.message || 'Failed to load events');
      toast.error(err.message || 'Failed to load events');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateClick = () => {
    setSelectedEvent(null);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleDialogSubmit = async (data: CreateEventRequest | UpdateEventRequest) => {
    try {
      if (selectedEvent) {
        await dispatch(updateEvent({ id: selectedEvent._id, data: data as UpdateEventRequest })).unwrap();
      } else {
        await dispatch(createEvent({ ...(data as CreateEventRequest), businessId: business._id })).unwrap();
      }
      handleDialogClose();
      loadEvents();
    } catch (err) {
      console.error('Failed to save event:', err);
    }
  };

  const handleViewClick = (event: ConferenceEvent) => {
    navigate(`/events/${event._id}`);
  };

  const handleEditClick = (event: ConferenceEvent) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  const handleDeleteClick = (event: ConferenceEvent) => {
    setEventToDelete(event);
    setDeleteDialogOpen(true);
  };

  const handleToggleActiveClick = async (event: ConferenceEvent) => {
    try {
      if (event.isActive) {
        await eventService.markInactiveEvent(event._id);
        toast.success(`"${event.eventName}" marked as inactive`);
      }
      loadEvents();
    } catch (err: any) {
      toast.error(err.message || 'Failed to update event status');
    }
  };

  const handleDeleteConfirm = async () => {
    if (eventToDelete) {
      try {
        await eventService.deleteEvent(eventToDelete._id);
        toast.success(`Event deleted successfully`);
        setDeleteDialogOpen(false);
        setEventToDelete(null);
        loadEvents();
      } catch (err: any) {
        toast.error(err.message || 'Failed to delete event');
      }
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
          <TextField
            fullWidth
            select
            size="small"
            label="Category"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="all">All Categories</MenuItem>
            {EVENT_CATEGORIES.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
          <TextField
            fullWidth
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </Grid>

        <Grid size={{ xs: 12, lg: 3 }} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', lg: 'flex-end' }, alignItems: 'center' }}>
          <Button variant="contained" startIcon={<Add />} onClick={handleCreateClick}>
            Create New Event
          </Button>
        </Grid>
      </Grid>

      {isLoading ? (
        <Grid container spacing={3}>
          {Array.from({ length: pageSize }).map((_, i) => (
            <Grid key={i} size={{ xs: 12, sm: 12, md: 6 }}>
              <CardSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : events.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 3,
          }}
        >
          <CalendarToday sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No events found
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            Try adjusting your filters or create a new event for {business.businessName}.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={handleCreateClick}>
            Create New Event
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {events.map((event) => (
            <Grid key={event._id} size={{ xs: 12, sm: 12, md: 6 }}>
              <EventCard
                event={event}
                onView={handleViewClick}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                onToggleActive={handleToggleActiveClick}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      <EventDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleDialogSubmit}
        event={selectedEvent}
        isLoading={isCreating || isUpdating}
        defaultBusiness={business}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Event"
        message={`Are you sure you want to delete "${eventToDelete?.eventName}"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setEventToDelete(null);
        }}
        severity="error"
      />
    </Box>
  );
};
